import { useEffect, useRef, useState } from "react";

import type { TaskStatus } from "../data/types";
import { hasStopped } from "../lib/status";

// Both drawings are copied verbatim out of `public/`, so these are the paths
// they are served at. `favicon.svg` is the one `index.html` ships and the one
// the push service worker names, and it is what the tab goes back to.
const RESTING = "/favicon.svg";
const ATTENTION = "/favicon-attention.svg";

/** Is this tab the one on screen? Not the same as the window having focus. */
const isTabVisible = (): boolean => document.visibilityState === "visible";

/**
 * Badge the tab's icon when the task it shows stops working while its user is
 * somewhere else.
 *
 * People run Tinsmith with a tab open per task, so the tab strip doubles as their
 * task list — the same reason each tab is named for its task
 * (`useDocumentTitle`). A narrow tab ellipsizes its title away to nothing but
 * the icon, which makes the icon the only part of that list that survives
 * having a dozen tasks open, and the only part of it worth putting a state in.
 *
 * What it puts there is "come back and look at me", not a mirror of the task's
 * status, and the difference is the whole of the logic below. The badge marks
 * an **unseen change**, so:
 *
 * - Returning to the tab clears it, acted on or not. Looking is the whole ask;
 *   a badge that stayed lit until the user did something about it would be a
 *   nag, and the page they are now looking at states the status anyway.
 * - A change that happens while they are already on the tab never lights it.
 *   They watched it happen — which is also what keeps the badge off the
 *   transitions they caused themselves, since cancelling a task or marking it
 *   done means being on its page to do so. The same status arriving because a
 *   *teammate* did it while they were away is news, and does light it.
 * - Every stop is its own event, whether or not the last one was seen. The
 *   status alone cannot carry that: two turns in a row commonly end on the
 *   *same* status (a back-and-forth review, or `needs_input` twice), and — the
 *   sharp case — a backgrounded tab, which is exactly the tab this badge is
 *   for, never renders the brief `running` between them, so the status it sees
 *   goes straight `review → review`. Keyed on the status value alone that reads
 *   as nothing having happened, and the second stop never badges. So this also
 *   takes `activityAt` (the task's `last_activity_at`, which advances every
 *   turn) and re-arms on a change in *either*: a new turn is news even when it
 *   lands on the status the last one did. Taking the status too still matters —
 *   `needs_input → review` is a real edge (agents-and-tasks.md §Turn
 *   boundaries) that a fresh `activityAt` and an unchanged status would miss if
 *   the timestamps ever coincided.
 * - The status a tab opens onto is not a change. Nothing happened; the tab was
 *   just opened, and ⌘-clicking a column of finished tasks off the board should
 *   not light every one of them.
 *
 * Visibility and not focus, because the question is which tab is on screen. A
 * task that stops while its own tab is selected has been seen even if the
 * browser is behind an editor — switching back to it shows the task page, badge
 * or no badge.
 *
 * Swapping the `href` of the document's existing icon link is the whole
 * mechanism: both drawings are static files, so there is no canvas, no
 * rasterizing an SVG that declares no intrinsic size, and no `data:` URL for
 * the Content-Security-Policy to have an opinion about.
 */
export const useFaviconAttention = (
  status: TaskStatus | undefined,
  // Spelled `| undefined` rather than `?`, to match `status` and because an
  // omitted one is not a neutral default: it pins the key to the status alone,
  // which is exactly the bug the argument was added to fix. A caller that has
  // no timestamp yet has to say so.
  activityAt: number | undefined,
): void => {
  // These two together are "the status a tab opens onto is not news": the tab
  // starts with nothing to report, and the ref below is what stops the first
  // status from becoming the first report. Neither works without the other —
  // start `seen` at false and a tab opened onto a stopped task badges itself.
  const [seen, setSeen] = useState(true);
  const hasBaseline = useRef(false);

  // Each event the task produces starts unseen, unless the user is here to
  // watch it arrive. Keyed on the status *and* `activityAt`, so a new turn is
  // its own event even when it ends on the status the last one did — the case a
  // status-only key drops, because a backgrounded tab never renders the
  // `running` between two `review`s.
  useEffect(() => {
    if (status === undefined) {
      return;
    }
    // The baseline is the first *defined* status, not the first run: the task
    // has to be fetched, so the hook's first render sees `undefined` and a
    // plain first-run guard would spend itself on that and treat the status
    // that followed — the one the tab was opened onto — as an event.
    if (!hasBaseline.current) {
      hasBaseline.current = true;
      return;
    }
    setSeen(isTabVisible());
  }, [status, activityAt]);

  // Coming back to the tab is what "seen" means. Registered once and left up
  // for the life of the task view: the listener is what turns a badge off, so
  // it has to be there for the badge the user has not come back to yet.
  useEffect(() => {
    const onVisibilityChange = (): void => {
      if (isTabVisible()) {
        setSeen(true);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const badged = status !== undefined && hasStopped(status) && !seen;
  useEffect(() => {
    // Queried per run rather than cached in a ref: the element belongs to the
    // document rather than to this hook, so a handle held across renders is a
    // handle that goes stale the day `index.html` grows a second icon link.
    // One `querySelector` per status change costs nothing against that.
    const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      return undefined;
    }
    // `setAttribute` and not `link.href`, which reflects back as an absolute
    // URL and would make every reader of this attribute origin-dependent.
    link.setAttribute("href", badged ? ATTENTION : RESTING);
    // Runs on the way out of the task view, which is what un-badges the tab
    // when the user navigates to the board. On a mere change of `badged` it
    // writes the resting icon a moment before the effect writes the real one,
    // which costs a redundant attribute write and nothing else.
    return () => {
      link.setAttribute("href", RESTING);
    };
  }, [badged]);
};
